import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import {
  PartnerEmptyState,
  PartnerFilterBar,
  PartnerLoading,
  PartnerNote,
  PartnerPageHeader,
  PartnerPageShell,
  partnerLinkSx,
  partnerOxbloodBtnSx,
} from '../components/PartnerListChrome';
import { DOCUMENT } from '../constants/documentTheme';
import usePartnerMobileLayout from '../hooks/usePartnerMobileLayout';
import InventoryItemDetails from '../components/InventoryItemDetails';
import PartnerCatalogItemDialog, { catalogFieldSx as fieldSx } from '../components/PartnerCatalogItemDialog';
import { isUsableImageUrl } from '../utils/itineraryItemImage';
import { formatInventoryPriceLabel } from '../utils/inventoryPrice';
import { CATALOG_CONFIGS } from '../utils/partnerCatalog';

const PARTNER_AUTH = { usePartnerAuth: true };

/**
 * Partner catalog page shared by Inventory and Add-ons. `kind` picks the API,
 * categories and copy from CATALOG_CONFIGS.
 */
const PartnerCatalogPage = ({ kind }) => {
  const config = CATALOG_CONFIGS[kind];
  const { t } = useTranslation();
  const isMobile = usePartnerMobileLayout();
  const [items, setItems] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [attachOpen, setAttachOpen] = useState(false);
  const [attachItem, setAttachItem] = useState(null);
  const [attachPackageId, setAttachPackageId] = useState('');
  const [attachBusy, setAttachBusy] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const copy = useCallback((key) => t(`${config.i18n}.${key}`), [t, config.i18n]);
  const categoryLabel = useCallback(
    (category) => t(`${config.i18n}.category.${category}`),
    [t, config.i18n]
  );
  const testId = (suffix) => `${config.testIdPrefix}-${suffix}`;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [catalogRes, pkgRes] = await Promise.all([
        api.get(`${config.endpoint}/items`, PARTNER_AUTH),
        api.get('/partner-packages/my-packages?limit=100', PARTNER_AUTH).catch(() => ({ data: { packages: [] } })),
      ]);
      setItems(catalogRes.data.items || []);
      setPackages(pkgRes.data.packages || pkgRes.data || []);
    } catch (err) {
      setError(err.response?.data?.error || copy('loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [config.endpoint, copy]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => ({
    all: items.length,
    ...config.categories.reduce((acc, cat) => {
      acc[cat] = items.filter((item) => item.category === cat).length;
      return acc;
    }, {}),
  }), [items, config.categories]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((item) => {
      if (filter !== 'all' && item.category !== filter) return false;
      if (!q) return true;
      return [item.name, item.description, item.notes, item.category]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(q));
    });
  }, [items, filter, search]);

  const toggleExpanded = (itemId) => {
    setExpandedId((prev) => (prev === itemId ? null : itemId));
  };

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setDialogOpen(true);
  };

  const handleSaved = async () => {
    setDialogOpen(false);
    await load();
  };

  const archiveItem = async (item) => {
    try {
      await api.delete(`${config.endpoint}/items/${item.id}`, PARTNER_AUTH);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || copy('archiveFailed'));
    }
  };

  const attachToPackage = async () => {
    try {
      setAttachBusy(true);
      setError('');
      await api.post(
        `${config.endpoint}/items/${attachItem.id}/attach-to-package`,
        { packageId: Number(attachPackageId) },
        PARTNER_AUTH
      );
      setAttachOpen(false);
      setAttachItem(null);
      setAttachPackageId('');
    } catch (err) {
      setError(err.response?.data?.error || t('partnerInventory.attachFailed'));
    } finally {
      setAttachBusy(false);
    }
  };

  if (loading) {
    return (
      <PartnerPageShell>
        <PartnerLoading />
      </PartnerPageShell>
    );
  }

  const rowColumns = (hasImage) => {
    const middle = config.showCapacity ? '7rem 6rem' : '7rem';
    return hasImage ? `4.5rem 1.4fr ${middle} 1fr auto` : `1.4fr ${middle} 1fr auto`;
  };

  return (
    <PartnerPageShell error={error} onClearError={() => setError('')}>
      <PartnerPageHeader
        title={copy('title')}
        subtitle={copy('subtitle')}
        actions={!isMobile ? (
          <Button sx={partnerOxbloodBtnSx} onClick={openCreate} data-testid={testId('new')}>
            {copy('newItem')}
          </Button>
        ) : null}
      />

      {isMobile && (
        <PartnerNote>{copy('desktopHint')}</PartnerNote>
      )}

      <PartnerFilterBar
        chips={[
          { id: 'all', label: t('partnerLists.filterAll'), count: counts.all },
          ...config.categories.map((cat) => ({
            id: cat,
            label: categoryLabel(cat),
            count: counts[cat] || 0,
          })),
        ]}
        activeChip={filter}
        onChipChange={setFilter}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={copy('search')}
      />

      {visible.length === 0 ? (
        <PartnerEmptyState
          title={copy('emptyTitle')}
          body={copy('emptyBody')}
          action={!isMobile ? (
            <Button sx={partnerOxbloodBtnSx} onClick={openCreate}>
              {copy('newItem')}
            </Button>
          ) : null}
        />
      ) : (
        <Box sx={{ mt: 2, borderTop: `1px solid ${DOCUMENT.ruleHard}` }}>
          {visible.map((item) => {
            const hasImage = isUsableImageUrl(item.imageUrl);
            const isExpanded = expandedId === item.id;
            return (
              <Box
                key={item.id}
                data-testid={testId(`row-${item.id}`)}
                sx={{
                  borderBottom: `1px solid ${DOCUMENT.rule}`,
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr auto',
                      md: rowColumns(hasImage),
                    },
                    gap: 1.5,
                    py: 2,
                    alignItems: 'center',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: `color-mix(in srgb, ${DOCUMENT.ink} 3%, transparent)`,
                    },
                  }}
                  onClick={() => toggleExpanded(item.id)}
                >
                  {hasImage ? (
                    <Box
                      component="img"
                      src={item.imageUrl}
                      alt=""
                      sx={{
                        width: 72,
                        height: 54,
                        objectFit: 'cover',
                        display: { xs: 'none', md: 'block' },
                        bgcolor: DOCUMENT.rule,
                      }}
                    />
                  ) : null}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 18 }}>
                      {item.name}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                      {categoryLabel(item.category)}
                    </Typography>
                    {item.description && !isExpanded ? (
                      <Typography
                        sx={{
                          fontSize: 13,
                          color: DOCUMENT.ink2,
                          mt: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {item.description}
                      </Typography>
                    ) : null}
                  </Box>
                  <Typography sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatInventoryPriceLabel(item, t)}
                  </Typography>
                  {config.showCapacity ? (
                    <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3 }}>
                      {item.capacity ? t('partnerInventory.capacity', { count: item.capacity }) : '—'}
                    </Typography>
                  ) : null}
                  <Box
                    sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <Box
                      component="button"
                      type="button"
                      onClick={() => openEdit(item)}
                      sx={partnerLinkSx}
                    >
                      {t('partnerInventory.edit')}
                    </Box>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => {
                        setAttachItem(item);
                        setAttachPackageId('');
                        setAttachOpen(true);
                      }}
                      sx={partnerLinkSx}
                      data-testid={testId(`attach-${item.id}`)}
                    >
                      {t('partnerInventory.addToPackage')}
                    </Box>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => archiveItem(item)}
                      sx={partnerLinkSx}
                      data-testid={testId(`archive-${item.id}`)}
                    >
                      {t('partnerInventory.archive')}
                    </Box>
                  </Box>
                  <Box
                    component="button"
                    type="button"
                    aria-expanded={isExpanded}
                    aria-label={isExpanded
                      ? t('partnerInventory.collapseDetails')
                      : t('partnerInventory.expandDetails')}
                    data-testid={testId(`expand-${item.id}`)}
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleExpanded(item.id);
                    }}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 36,
                      height: 36,
                      p: 0,
                      m: 0,
                      border: 0,
                      bgcolor: 'transparent',
                      color: DOCUMENT.ink3,
                      cursor: 'pointer',
                      justifySelf: 'end',
                    }}
                  >
                    <ExpandMoreIcon
                      sx={{
                        fontSize: 22,
                        transform: isExpanded ? 'rotate(180deg)' : 'none',
                        transition: 'transform 0.2s ease',
                      }}
                    />
                  </Box>
                </Box>
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <InventoryItemDetails
                    item={item}
                    testId={testId(`row-${item.id}-details`)}
                  />
                </Collapse>
              </Box>
            );
          })}
        </Box>
      )}

      <PartnerCatalogItemDialog
        open={dialogOpen}
        kind={kind}
        item={editing}
        onClose={() => setDialogOpen(false)}
        onSaved={handleSaved}
      />

      <Dialog open={attachOpen} onClose={() => setAttachOpen(false)} fullWidth maxWidth="xs" PaperProps={{ sx: { bgcolor: DOCUMENT.surface, borderRadius: 0 } }}>
        <DialogTitle sx={{ fontFamily: DOCUMENT.display, fontWeight: 400 }}>
          {t('partnerInventory.addToPackage')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2, mb: 2 }}>
            {attachItem?.name}
          </Typography>
          <TextField
            select
            fullWidth
            label={t('partnerInventory.selectPackage')}
            value={attachPackageId}
            onChange={(e) => setAttachPackageId(e.target.value)}
            variant="standard"
            sx={fieldSx}
          >
            {(Array.isArray(packages) ? packages : []).map((pkg) => (
              <MenuItem key={pkg.id} value={String(pkg.id)}>
                {pkg.title || pkg.name || `#${pkg.id}`}
              </MenuItem>
            ))}
          </TextField>
          {!packages?.length && (
            <Alert severity="info" sx={{ mt: 2 }}>{copy('noPackages')}</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAttachOpen(false)}>{t('common.cancel')}</Button>
          <Button
            sx={partnerOxbloodBtnSx}
            disabled={attachBusy || !attachPackageId}
            onClick={attachToPackage}
          >
            {attachBusy ? t('common.saving') : t('partnerInventory.attach')}
          </Button>
        </DialogActions>
      </Dialog>
    </PartnerPageShell>
  );
};

export default PartnerCatalogPage;
